import {
    buildVerticalSpacingKey,
    isValidVerticalSpacing,
    parseVerticalSpacingKey,
} from '../helpers/verticalSpacing';

function VSFullIcon() {
    return (
        <svg width="20" height="16" viewBox="0 0 24 16" aria-hidden="true">
            <rect
                x="1"
                y="1"
                width="22"
                height="14"
                rx="3"
                fill="white"
                stroke="rgb(229,231,235)"
            />
            <rect
                x="2"
                y="2"
                width="20"
                height="12"
                rx="2"
                fill="currentColor"
            />
        </svg>
    );
}

function VSIcon({ area = 'top', size = 'medium' }) {
    const H = { none: 0, medium: 4, large: 7 }[size] ?? 0;
    const y = area === 'top' ? 2 : 16 - H - 2;

    return (
        <svg width="20" height="16" viewBox="0 0 24 16" aria-hidden="true">
            <rect
                x="1"
                y="1"
                width="22"
                height="14"
                rx="3"
                fill="white"
                stroke="rgb(229,231,235)"
            />
            {H > 0 && (
                <rect
                    x="2"
                    y={y}
                    width="20"
                    height={H}
                    rx="2"
                    fill="currentColor"
                />
            )}
        </svg>
    );
}

function VSButtonRect({ active, disabled, srText, children, onClick }) {
    const colorClass = disabled
        ? 'text-neutral-300'
        : active
        ? 'text-primary-600'
        : 'text-neutral-500';

    return (
        <span
            className={[
                'group relative inline-flex rounded-xl p-[2px]',
                'transition-colors duration-300 ease-out',
                disabled
                    ? 'bg-neutral-200'
                    : active
                    ? 'bg-primary-600'
                    : 'bg-neutral-300 hover:bg-gradient-to-br hover:from-primary-600 hover:to-secondary-600',
                !disabled &&
                    'focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600',
            ].join(' ')}
        >
            <button
                type="button"
                disabled={disabled}
                onClick={disabled ? undefined : onClick}
                aria-pressed={active}
                className={[
                    'inline-flex h-9 w-12 items-center justify-center rounded-[10px] bg-white',
                    'transition-[opacity,box-shadow] duration-300 ease-out',
                    'shadow-sm',
                    !disabled && 'hover:shadow',
                    disabled ? 'cursor-default opacity-50' : '',
                    active
                        ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                        : 'ring-2 ring-neutral-200',
                    'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1',
                    colorClass,
                ].join(' ')}
            >
                <span className="sr-only">{srText}</span>
                {children}
            </button>
        </span>
    );
}

export default function VerticalSpacingRow({
    value = 'medium',
    onChange,
    hideTop = false,
    hideBottom = false,
    topFixed,
    bottomFixed,
    allowFull = false,
    disabled = false,
}) {
    const normalizedValue = String(value ?? '')
        .trim()
        .toLowerCase();
    const isFullMode =
        allowFull &&
        (normalizedValue === '' ||
            normalizedValue === 'full' ||
            normalizedValue === 'default');

    const { top, bottom } = parseVerticalSpacingKey(
        isFullMode ? 'medium' : value
    );
    const CHOICES = ['none', 'medium', 'large'];
    const SR = { none: 'None', medium: 'Medium', large: 'Large' };
    const effTop = topFixed ?? top;
    const effBottom = bottomFixed ?? bottom;

    const setTop = (t) => {
        if (disabled) return;
        if (!isValidVerticalSpacing(t, effBottom)) return;
        const key = buildVerticalSpacingKey(t, effBottom);
        if (key) onChange?.(key);
    };

    const setBottom = (b) => {
        if (disabled) return;
        if (!isValidVerticalSpacing(effTop, b)) return;
        const key = buildVerticalSpacingKey(effTop, b);
        if (key) onChange?.(key);
    };

    const setFull = () => {
        if (disabled) return;
        if (!allowFull) return;
        onChange?.('');
    };

    const controlsMutedClass = isFullMode
        ? 'opacity-60 transition-opacity'
        : '';

    const disabledStyles = disabled ? 'opacity-50 pointer-events-none' : '';

    return (
        <div className={`space-y-3 ${disabledStyles}`}>
            {allowFull && (
                <div className="flex items-center gap-3">
                    <VSButtonRect
                        active={isFullMode}
                        srText="Full (no vertical spacing)"
                        onClick={disabled ? undefined : setFull}
                    >
                        <VSFullIcon />
                    </VSButtonRect>
                    <span className="text-xs font-medium text-neutral-600">
                        Full no padding
                    </span>
                </div>
            )}

            {!hideTop && (
                <div>
                    <div className="mb-1 text-[11px] font-medium text-neutral-600">
                        Top
                    </div>
                    <div className={`inline-flex gap-2 ${controlsMutedClass}`}>
                        {CHOICES.map((opt) => (
                            <VSButtonRect
                                key={`top-${opt}`}
                                active={!isFullMode && effTop === opt}
                                disabled={
                                    disabled ||
                                    !isValidVerticalSpacing(opt, effBottom)
                                }
                                srText={`Top ${SR[opt]}`}
                                onClick={() => setTop(opt)}
                            >
                                <VSIcon area="top" size={opt} />
                            </VSButtonRect>
                        ))}
                    </div>
                </div>
            )}

            {!hideBottom && (
                <div>
                    <div className="mb-1 text-[11px] font-medium text-neutral-600">
                        Bottom
                    </div>
                    <div className={`inline-flex gap-2 ${controlsMutedClass}`}>
                        {CHOICES.map((opt) => (
                            <VSButtonRect
                                key={`bot-${opt}`}
                                active={!isFullMode && effBottom === opt}
                                disabled={
                                    disabled ||
                                    !isValidVerticalSpacing(top, opt)
                                }
                                srText={`Bottom ${SR[opt]}`}
                                onClick={() => setBottom(opt)}
                            >
                                <VSIcon area="bottom" size={opt} />
                            </VSButtonRect>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
